import React, { createContext, useContext, useCallback, useRef } from 'react'
import { DataCache, DataManagerConfig, ServiceProvider } from '../types/dataManager'
import { 
  getServiceInstance, 
  resolveAssetProvider, 
  resolveProjectProvider, 
  resolveProjectFromAssetProvider,
  getCachedProvider 
} from '../services/dataServiceFactory'

interface DataProviderContextType {
  cache: React.MutableRefObject<DataCache>
  config: DataManagerConfig
  resolveAssetProvider: (uuid: string) => Promise<ServiceProvider>
  resolveProjectProvider: (projectId: string) => Promise<ServiceProvider>
  resolveProjectFromAssetProvider: (uuid: string) => Promise<ServiceProvider>
  getCachedProvider: (uuid: string, type: 'asset' | 'project') => ServiceProvider | null
  getService: (provider: ServiceProvider) => ReturnType<typeof getServiceInstance>
  clearCache: (type?: 'assets' | 'projects' | 'all') => void
  isCacheValid: (key: string, timestamp: number) => boolean
}

const DataProviderContext = createContext<DataProviderContextType | null>(null)

// Default configuration
const DEFAULT_CONFIG: DataManagerConfig = {
  cacheTimeout: 5 * 60 * 1000, // 5 minutes
  retryAttempts: 3,
  retryDelay: 1000 // 1 second
}

interface DataProviderProps {
  children: React.ReactNode
  config?: Partial<DataManagerConfig>
}

export const DataProvider: React.FC<DataProviderProps> = ({ 
  children, 
  config: userConfig = {} 
}) => {
  const config = { ...DEFAULT_CONFIG, ...userConfig }
  
  // Initialize cache
  const cache = useRef<DataCache>({
    assets: new Map(),
    projects: new Map(),
    assetCores: new Map(),
    providerMap: new Map(),
    lastFetched: new Map()
  })

  // Cache validation
  const isCacheValid = useCallback((_key: string, timestamp: number): boolean => {
    return Date.now() - timestamp < config.cacheTimeout
  }, [config.cacheTimeout])

  // Clear cache function
  const clearCache = useCallback((type?: 'assets' | 'projects' | 'all') => {
    const { assets, projects, assetCores, providerMap, lastFetched } = cache.current
    
    switch (type) {
      case 'assets':
        assets.clear()
        assetCores.clear()
        // Clear asset-related entries from other maps
        Array.from(providerMap.keys()).forEach(key => {
          if (!key.startsWith('project_')) {
            providerMap.delete(key)
          }
        })
        Array.from(lastFetched.keys()).forEach(key => {
          if (key.startsWith('asset_') || key.startsWith('assetCore_') || key.startsWith('provider_')) {
            lastFetched.delete(key)
          }
        })
        break
      case 'projects':
        projects.clear()
        // Clear project-related entries from other maps
        Array.from(providerMap.keys()).forEach(key => {
          if (key.startsWith('project_')) {
            providerMap.delete(key)
          }
        })
        Array.from(lastFetched.keys()).forEach(key => {
          if (key.startsWith('project_')) {
            lastFetched.delete(key)
          }
        })
        break
      case 'all':
      default:
        assets.clear()
        projects.clear()
        assetCores.clear()
        providerMap.clear()
        lastFetched.clear()
        break
    }
  }, [])

  // Wrapper functions that use the service factory
  const resolveAssetProviderWrapper = useCallback(async (uuid: string): Promise<ServiceProvider> => {
    return resolveAssetProvider(uuid)
  }, [])

  const resolveProjectProviderWrapper = useCallback(async (projectId: string): Promise<ServiceProvider> => {
    return resolveProjectProvider(projectId)
  }, [])

  const resolveProjectFromAssetProviderWrapper = useCallback(async (uuid: string): Promise<ServiceProvider> => {
    return resolveProjectFromAssetProvider(uuid)
  }, [])

  const getCachedProviderWrapper = useCallback((uuid: string, type: 'asset' | 'project'): ServiceProvider | null => {
    return getCachedProvider(uuid, type)
  }, [])

  const getService = useCallback((provider: ServiceProvider) => {
    return getServiceInstance(provider)
  }, [])

  const contextValue: DataProviderContextType = {
    cache,
    config,
    resolveAssetProvider: resolveAssetProviderWrapper,
    resolveProjectProvider: resolveProjectProviderWrapper,
    resolveProjectFromAssetProvider: resolveProjectFromAssetProviderWrapper,
    getCachedProvider: getCachedProviderWrapper,
    getService,
    clearCache,
    isCacheValid
  }

  return (
    <DataProviderContext.Provider value={contextValue}>
      {children}
    </DataProviderContext.Provider>
  )
}

// Hook to use the data provider context
export const useDataProvider = (): DataProviderContextType => {
  const context = useContext(DataProviderContext)
  if (!context) {
    throw new Error('useDataProvider must be used within a DataProvider')
  }
  return context
}

// Hook to get a specific service instance
export const useServiceInstance = (provider: ServiceProvider) => {
  const { getService } = useDataProvider()
  return getService(provider)
}

// Hook to get cached data
export const useCachedData = () => {
  const { cache, isCacheValid } = useDataProvider()
  
  const getCachedAsset = useCallback((uuid: string) => {
    const asset = cache.current.assets.get(uuid)
    const timestamp = cache.current.lastFetched.get(`asset_${uuid}`)
    if (asset && timestamp && isCacheValid(`asset_${uuid}`, timestamp)) {
      return asset
    }
    return null
  }, [cache, isCacheValid])

  const getCachedProject = useCallback((projectId: string) => {
    const project = cache.current.projects.get(projectId)
    const timestamp = cache.current.lastFetched.get(`project_${projectId}`)
    if (project && timestamp && isCacheValid(`project_${projectId}`, timestamp)) {
      return project
    }
    return null
  }, [cache, isCacheValid])

  const getCachedProjectByAsset = useCallback((assetUuid: string) => {
    const project = cache.current.projects.get(`asset_${assetUuid}`)
    const timestamp = cache.current.lastFetched.get(`project_asset_${assetUuid}`)
    if (project && timestamp && isCacheValid(`project_asset_${assetUuid}`, timestamp)) {
      return project
    }
    return null
  }, [cache, isCacheValid])

  const getCachedAssetCore = useCallback((uuid: string) => {
    const assetCore = cache.current.assetCores.get(uuid)
    const timestamp = cache.current.lastFetched.get(`assetCore_${uuid}`)
    if (assetCore && timestamp && isCacheValid(`assetCore_${uuid}`, timestamp)) {
      return assetCore
    }
    return null
  }, [cache, isCacheValid])

  return {
    getCachedAsset,
    getCachedProject,
    getCachedProjectByAsset,
    getCachedAssetCore
  }
}